import PropTypes from 'prop-types';
import { makeStyles, Typography, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { useSelector } from 'react-redux';
import RoundedButton from 'components/Shared/RoundedButton';
import { Badge } from 'components/Shared/Badge';
import { getTransitionWithVariable } from 'redux_store';
import { RESULT_STATE, selectResultState, getClaimUploadStatusColor, CLAIM_UPLOAD_STATUS_LABEL_KEY } from './lib';

const useStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  fileBlock: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), minWidth: 0 },
  fileIcon: { color: theme.palette.text.secondary },
  fileName: { fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headerActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexShrink: 0 },
  // stats
  stats: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(1) },
  stat: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
  },
  statLabel: { color: theme.palette.text.secondary, fontSize: 13 },
  statValue: { fontSize: 28, fontWeight: 700, lineHeight: 1.2 },
  imported: { color: theme.palette.success.main },
  failed: { color: theme.palette.error.main },
  note: { color: theme.palette.text.secondary, fontSize: 13, marginTop: theme.spacing(1) },
  summary: { fontSize: 15, fontWeight: 500, marginBottom: theme.spacing(2) },
  successHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  successIcon: { color: theme.palette.success.main },
  successTitle: { fontWeight: 600, fontSize: 15, color: theme.palette.success.main },
  downloadBtn: { marginTop: theme.spacing(2) },
  // rejected / error list
  errorHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5) },
  rejectedSub: { color: theme.palette.text.secondary, fontSize: 13, marginBottom: theme.spacing(1.5) },
  errorIcon: { color: theme.palette.error.main },
  errorTitle: { fontWeight: 600, color: theme.palette.error.main },
  issueCount: { color: theme.palette.text.secondary, fontSize: 13, marginLeft: 'auto' },
  errorList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 240,
    overflowY: 'auto',
  },
  errorRow: {
    padding: theme.spacing(1, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:first-child': { borderTop: 'none' },
  },
  errorLoc: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  errorMsg: { fontSize: 14, color: theme.palette.text.primary },
  errorEmpty: { padding: theme.spacing(1.5), color: theme.palette.text.secondary, fontSize: 13 },
}));

const ClaimUploadResult = ({ response, validateOnly, onClose }) => {
  const classes = useStyles();
  const {
    en: { claimUpload },
  } = useSelector(state => state.i18n);
  const state = selectResultState(response, { validateOnly });
  if (!state) return null;

  const {
    fileName,
    totalRows = 0,
    imported = 0,
    failed = 0,
    skippedEmptyRows = 0,
    validationErrors = [],
    errorFileUrl,
    status,
  } = response;

  // Plain-language outcome for an actual import (rows persisted).
  const getSummary = () =>
    failed > 0
      ? getTransitionWithVariable(claimUpload.summaryImportPartial, { imported, failed })
      : getTransitionWithVariable(claimUpload.summaryImportComplete, { imported, total: totalRows });

  const Stats = (
    <div className={classes.stats}>
      <div className={classes.stat}>
        <Typography className={classes.statLabel}>{claimUpload.resultTotal}</Typography>
        <Typography className={classes.statValue}>{totalRows}</Typography>
      </div>
      <div className={classes.stat}>
        <Typography className={classes.statLabel}>{claimUpload.resultImported}</Typography>
        <Typography className={`${classes.statValue} ${classes.imported}`}>{imported}</Typography>
      </div>
      <div className={classes.stat}>
        <Typography className={classes.statLabel}>{claimUpload.resultFailed}</Typography>
        <Typography className={`${classes.statValue} ${classes.failed}`}>{failed}</Typography>
      </div>
    </div>
  );

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div className={classes.fileBlock}>
          <InsertDriveFileOutlinedIcon fontSize="small" className={classes.fileIcon} />
          <Typography className={classes.fileName} title={fileName}>
            {fileName}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          {status && (
            <Badge
              label={claimUpload[CLAIM_UPLOAD_STATUS_LABEL_KEY[status]] || status}
              colorType={getClaimUploadStatusColor(status)}
            />
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {state === RESULT_STATE.REJECTED ? (
        <>
          {!validateOnly && Stats}
          <div className={classes.errorHead}>
            <ErrorOutlineIcon fontSize="small" className={classes.errorIcon} />
            <Typography className={classes.errorTitle}>{claimUpload.rejectedTitle}</Typography>
            {validationErrors.length > 0 && (
              <span className={classes.issueCount}>
                {getTransitionWithVariable(claimUpload.issuesCount, { count: validationErrors.length })}
              </span>
            )}
          </div>
          <Typography className={classes.rejectedSub}>
            {validateOnly ? claimUpload.rejectedSubtitleValidate : claimUpload.rejectedSubtitle}
          </Typography>
          {validationErrors.length > 0 ? (
            <div className={classes.errorList}>
              {validationErrors.map((err, i) => {
                const idx = err.indexOf(': ');
                const loc = idx > -1 ? err.slice(0, idx) : null;
                const msg = idx > -1 ? err.slice(idx + 2) : err;
                return (
                  <div key={i} className={classes.errorRow}>
                    {loc && <span className={classes.errorLoc}>{loc}</span>}
                    <span className={classes.errorMsg}>{msg}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <Typography className={classes.errorEmpty}>{claimUpload.noErrorDetails}</Typography>
          )}
        </>
      ) : validateOnly ? (
        failed > 0 ? (
          <>
            <div className={classes.errorHead}>
              <ErrorOutlineIcon fontSize="small" className={classes.errorIcon} />
              <Typography className={classes.errorTitle}>
                {getTransitionWithVariable(claimUpload.summaryValidateIssues, { failed, total: totalRows })}
              </Typography>
            </div>
            {errorFileUrl && (
              <RoundedButton
                variant="outlined"
                size="small"
                className={classes.downloadBtn}
                startIcon={<GetAppIcon />}
                onClick={() => window.open(errorFileUrl, '_blank', 'noopener')}
              >
                {getTransitionWithVariable(claimUpload.downloadErrorRows, { count: failed })}
              </RoundedButton>
            )}
          </>
        ) : (
          <div className={classes.successHead}>
            <CheckCircleOutlineIcon fontSize="small" className={classes.successIcon} />
            <Typography className={classes.successTitle}>{claimUpload.validatedSuccess}</Typography>
          </div>
        )
      ) : (
        <>
          {state === RESULT_STATE.COMPLETED ? (
            <div className={classes.successHead}>
              <CheckCircleOutlineIcon fontSize="small" className={classes.successIcon} />
              <Typography className={classes.successTitle}>{getSummary()}</Typography>
            </div>
          ) : (
            <Typography className={classes.summary}>{getSummary()}</Typography>
          )}
          {Stats}
          {skippedEmptyRows > 0 && (
            <Typography className={classes.note}>
              {getTransitionWithVariable(claimUpload.skippedEmptyRows, { count: skippedEmptyRows })}
            </Typography>
          )}
          {state === RESULT_STATE.COMPLETED_WITH_ERRORS && errorFileUrl && (
            <RoundedButton
              variant="outlined"
              size="small"
              className={classes.downloadBtn}
              startIcon={<GetAppIcon />}
              onClick={() => window.open(errorFileUrl, '_blank', 'noopener')}
            >
              {getTransitionWithVariable(claimUpload.downloadErrorRows, { count: failed })}
            </RoundedButton>
          )}
          <Typography className={classes.note}>{claimUpload.addedToHistory}</Typography>
        </>
      )}
    </div>
  );
};

ClaimUploadResult.propTypes = {
  response: PropTypes.object,
  validateOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

ClaimUploadResult.defaultProps = { response: null, validateOnly: false };

export default ClaimUploadResult;
